import React, { useState } from 'react';
import { ProjectProfile } from '@kaizen/shared';
import { AlertOctagon, CloudUpload, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import {
  kaizenDialogTransition,
  kaizenOverlayTransition
} from '../../lib/kaizenMotion';

interface ConfirmDeployModalProps {
  project: ProjectProfile;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const ConfirmDeployModal: React.FC<ConfirmDeployModalProps> = ({
  project,
  isOpen,
  onClose,
  onConfirm
}) => {
  const [confirmed, setConfirmed] = useState(false);

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={kaizenOverlayTransition}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 8,
              scale: 0.985
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }}
            exit={{
              opacity: 0,
              y: 6,
              scale: 0.99
            }}
            transition={kaizenDialogTransition}
            className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-md w-full overflow-hidden"
          >
        <div className="flex items-center justify-between px-5 py-4 border-b border-rose-100 bg-rose-50/50">
          <div className="flex items-center gap-2 text-rose-800 font-semibold text-sm">
            <AlertOctagon className="w-4 h-4 text-rose-600" />
            <span>Confirm Production Deployment</span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 text-xs">
          <p className="text-slate-600">
            You are about to deploy this project to its configured live <strong>Production</strong> environment.
          </p>

          <div className="p-3 bg-slate-50 border border-slate-200/80 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-500">Project:</span>
              <span className="font-semibold text-slate-800">{project.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Deployment:</span>

              <span className="font-mono text-slate-800">
                {project.config?.deployment?.mode === 'vercel_render'
                  ? 'Vercel + Render'
                  : (
                      project.cloudflareProjectName ||
                      'Cloudflare Pages'
                    )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Build Output:</span>
              <span className="font-mono text-slate-800">{project.buildOutputDirectory}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Target URL:</span>
              <span className="font-mono text-indigo-600 truncate max-w-[200px]">
                {project.productionUrl || 'https://*.pages.dev'}
              </span>
            </div>
          </div>

          <label className="flex items-start gap-2.5 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
              className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-slate-700 leading-tight">
              I verify this build passed local tests and preview inspection. Deploy to production now.
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3.5 bg-slate-50 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-800 rounded-lg"
          >
            Cancel
          </button>
          <button
            disabled={!confirmed}
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-lg text-white shadow-sm transition-colors ${
              confirmed
                ? 'bg-rose-600 hover:bg-rose-700'
                : 'bg-slate-300 cursor-not-allowed text-slate-500'
            }`}
          >
            <CloudUpload className="w-3.5 h-3.5" />
            <span>Deploy to Production</span>
          </button>
        </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
